import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDown, Search } from 'lucide-react';
import '../../Screens/Login-Screen/Login.css';

// Size map mirrors InputComponent's sizeClassMap exactly, same size prop = same visual size.
// The explicit !h-[..] locks the trigger to the input's natural height per size: an <input>
// sizes from its own line-height while this flex <button> sizes from its child's line-box, so
// without a pinned height the two drift by a couple px. Heights = line-height + py + border-b:
//   sm 24+8+2=34 · lg 28+16+2=46 · xl 32+24+2=58
const triggerSizeMap = {
    sm: '!text-base !py-1 !h-[34px]',
    lg: '!text-xl !py-2 !h-[46px]',
    xl: '!text-2xl !py-3 !h-[58px]',
};

const optionSizeMap = {
    sm: 'text-sm py-2 px-4',
    lg: 'text-base py-3 px-5',
    xl: 'text-lg py-4 px-6',
};

function Selector({
    options = [],
    value,
    onChange,
    placeholder = 'Select Service',
    size = 'lg',
    required = false,
    name,
    searchable = false,
    className = '',
    triggerClassName = ''
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef(null);
    const searchRef    = useRef(null);

    // 2. System Direction from Redux
    const { dir } = useSelector((state) => state.systemData || {});
    const effectiveDir = dir || 'ltr'; 

    const handleSelect = (option) => {
        const val = typeof option === 'object' ? (option.title || option.label) : option;
        onChange(val);
        setOpen(false);
        setQuery('');
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-focus search input when dropdown opens
    useEffect(() => {
        if (open && searchable) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
        if (!open) setQuery('');
    }, [open, searchable]);

    const handleToggle = () => setOpen(prev => !prev);

    const filteredOptions = searchable && query.trim()
        ? options.filter(opt => {
            const label = typeof opt === 'object' ? (opt.title || opt.label) : opt;
            return label.toLowerCase().includes(query.toLowerCase());
        })
        : options;

    return (
        <div
            ref={containerRef}
            dir={effectiveDir}
            className={`relative w-full ${className}`}
        >
            <button
                type="button"
                onClick={handleToggle}
                className={`selector-trigger ${open ? 'selector-trigger--open' : ''} ${triggerSizeMap[size] || triggerSizeMap.lg} ${triggerClassName}`}
            >
                <span className={`text-start truncate w-full ${!value ? 'text-zinc-400 font-normal' : 'font-medium'}`}>
                    {value || placeholder}
                </span>
                
                <ChevronDown 
                    className={`
                        w-5 h-5 transform transition-transform duration-300 
                        ${open ? 'rotate-180 text-primary-600' : 'text-zinc-400'} 
                        ${effectiveDir === 'rtl' ? 'mr-3' : 'ml-3'}
                    `} 
                />
            </button>

            {open && (
                <div
                    className={`absolute top-full left-0 right-0 mt-1 z-50 selector-dropdown animate-in fade-in zoom-in-95 duration-200 ${effectiveDir === 'rtl' ? 'text-right' : 'text-left'}`}
                >
                    {searchable && (
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100">
                            <Search className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            <input
                                ref={searchRef}
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Search..."
                                className="selector-search-input"
                            />
                        </div>
                    )}
                    <ul className="max-h-56 overflow-y-auto overscroll-contain">
                        {filteredOptions.length === 0 ? (
                            <li className="text-sm text-zinc-400 px-4 py-3 text-center">No results</li>
                        ) : filteredOptions.map((option, index) => {
                            const label = typeof option === 'string' ? option : (option.title || option.label);
                            const key = option.id || index;
                            const isSelected = value === label;

                            return (
                                <li
                                    key={key}
                                    onClick={() => handleSelect(option)}
                                    className={`
                                        cursor-pointer transition-all duration-200
                                        first:rounded-t-[14px] last:rounded-b-[14px]
                                        ${optionSizeMap[size] || optionSizeMap.lg}
                                        ${isSelected
                                            ? 'bg-primary-50 text-primary-700 font-bold'
                                            : 'text-zinc-700 hover:bg-zinc-50 hover:text-primary-600'}
                                    `}
                                >
                                    <div className="flex items-center justify-between">
                                        <span>{label}</span>
                                        {isSelected && (
                                            <div className="w-2 h-2 rounded-full bg-primary-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            
            {required && (
                <input
                    className="absolute opacity-0 pointer-events-none -z-10 bottom-0"
                    name={name}
                    value={value || ''}
                    readOnly
                    required
                />
            )}
        </div>
    );
}

export default Selector;